import { Info } from 'lucide-react'
import type { ReactNode } from 'react'
import { Table, TableBody, TableCell, TableRow } from 'shared/components/ui/table'
import { Tooltip, TooltipContent, TooltipTrigger } from 'shared/components/ui/tooltip'
import { cn } from 'shared/lib/utils'

/**
 * A page's small parts — the hub's look, every page's since the redesign: a
 * section's title with its count and, when it needs one, an explanation behind an
 * «i»; a compact bordered table for a short list beside the main one, and its one
 * row that says it is empty.
 */
export function SectionTitle({
	title,
	count,
	hint,
	aside,
}: {
	title: ReactNode
	count?: number
	hint?: ReactNode
	aside?: ReactNode
}) {
	return (
		<div className='flex min-h-7 flex-wrap items-center gap-2'>
			<h3 className='text-sm font-semibold'>{title}</h3>
			{count !== undefined && <span className='text-sm text-muted-foreground tabular-nums'>{count}</span>}
			{hint && <Hint>{hint}</Hint>}
			{aside && <div className='ml-auto flex flex-wrap items-center gap-2'>{aside}</div>}
		</div>
	)
}

/** An explanation that waits behind an «i» until it is asked for. */
export function Hint({ children }: { children: ReactNode }) {
	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<button type='button' className='text-muted-foreground hover:text-foreground' aria-label={String(children)}>
					<Info className='size-3.5' />
				</button>
			</TooltipTrigger>
			<TooltipContent>{children}</TooltipContent>
		</Tooltip>
	)
}

/** A short list as a table in one frame — the same look as `DataTable`, without its paging. */
export function CompactTable({ testId, children }: { testId?: string; children: ReactNode }) {
	return (
		<div className='overflow-hidden rounded-md border'>
			<Table data-testid={testId}>{children}</Table>
		</div>
	)
}

/**
 * An empty `CompactTable`'s body: the header stays, and one row across every column
 * says there is nothing — the same as `DataTable`'s.
 */
export function EmptyTableBody({ columns, children }: { columns: number; children: ReactNode }) {
	return (
		<TableBody>
			<TableRow className='hover:bg-transparent'>
				<TableCell colSpan={columns} className='h-16 text-center text-muted-foreground'>
					{children}
				</TableCell>
			</TableRow>
		</TableBody>
	)
}

/** A row of `Stat`s — a section's figures in one line, under its title. */
export function Stats({ testId, children }: { testId?: string; children: ReactNode }) {
	return (
		<div className='flex flex-wrap items-start gap-x-10 gap-y-3' data-testid={testId}>
			{children}
		</div>
	)
}

/**
 * One figure with its label above it — the hub's counts, every page's totals. A
 * count of 0 is quiet; above 0 a `tone` colours it: `warn` amber, `bad` red. The
 * currency or unit goes in the label («Сумма, сум»), not after the number.
 */
export function Stat({
	label,
	value,
	tone,
	testId,
	valueTestId,
}: {
	label: ReactNode
	value: ReactNode
	tone?: 'warn' | 'bad'
	testId?: string
	valueTestId?: string
}) {
	const zero = value === 0 || value === '0'
	return (
		<div className='flex flex-col' data-testid={testId}>
			<span className='text-xs text-muted-foreground first-letter:uppercase'>{label}</span>
			<span
				className={cn(
					'text-lg leading-tight font-semibold tabular-nums',
					zero && 'text-muted-foreground',
					!zero && tone === 'warn' && 'text-amber-700 dark:text-amber-400',
					!zero && tone === 'bad' && 'text-destructive',
				)}
				data-testid={valueTestId}
			>
				{value}
			</span>
		</div>
	)
}
